/** @format */

import { useRef, useLayoutEffect, useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Download, User, Bot, Headphones } from "lucide-react";

interface Message {
  from: string;
  body: string;
  dateSent?: string;
  type: "sent" | "received";
  isReply?: boolean;
  originalMessageSid?: string;
  replyToBody?: string;
  replyToMediaUrl?: string;
  replyToMediaType?: string;
  replyToFileName?: string;
  replyToFileSize?: number;
  imageUrl?: string;
  fileName?: string;
  messageType?: "text" | "image" | "audio" | "assistant" | "document";
  media_url?: string;
  media_type?: string;
  file_name?: string;
  file_size?: number;
  sid?: string;
  messageSid?: string;
}

interface ChatWindowProps {
  messages: Message[];
  isHistoryPage: boolean;
}

const findOriginalMessage = (messages: Message[], originalSid?: string) => {
  if (!originalSid) return null;
  return messages.find(msg => msg.sid === originalSid || msg.messageSid === originalSid);
};

const formatTime = (dateSent?: string, t?: any) => {
  if (!dateSent) return "";
  try {
    const messageDate = new Date(dateSent);
    const today = new Date();
    
    const isToday = messageDate.toDateString() === today.toDateString();
    
    if (isToday) {
      const time = messageDate.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${t?.("today") || "Today"} ${time}`;
    } else {
      return messageDate.toLocaleDateString([], {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  } catch {
    return "";
  }
};

const formatMessage = (message: string) => {
  if (!message || typeof message !== 'string') {
    return '';
  }
  
  const cleanedMessage = message.replace(/^\n+/, "").trim();
  const lines = cleanedMessage.split("\n");
  return lines
    .map((line) => {
      const trimmedLine = line.trim();
      if (trimmedLine.endsWith(":")) {
        return `\n${trimmedLine}\n`;
      } else if (trimmedLine.startsWith("-") || /^\d+\./.test(trimmedLine)) {
        return `  ${trimmedLine}\n`;
      }
      return `${trimmedLine}\n`;
    })
    .join("");
};

const MessageBubble = ({
  from,
  body,
  bgColor,
  textColor,
  alignRight,
  dateSent,
  isReply,
  replyToBody,
  replyToMediaUrl,
  replyToMediaType,
  replyToFileName,
  imageUrl,
  fileName,
  messageType,
  mediaUrl,
  originalMessageSid,
  sid,
  setMessageRef,
  scrollToMessage,
  t,
  allMessages,
}: {
  from: string;
  body: string;
  bgColor: string;
  textColor: string;
  alignRight: boolean;
  dateSent?: string;
  isReply?: boolean;
  replyToBody?: string;
  replyToMediaUrl?: string;
  replyToMediaType?: string;
  replyToFileName?: string;
  imageUrl?: string;
  fileName?: string;
  messageType?: "text" | "image" | "audio" | "assistant" | "document";
  mediaUrl?: string;
  originalMessageSid?: string;
  sid?: string;
  setMessageRef: (sid: string, el: HTMLDivElement | null) => void;
  scrollToMessage: (sid: string) => void;
  t: any;
  allMessages: Message[];
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hPadding, setHPadding] = useState<number>(12);
  const [imageError, setImageError] = useState(false);
  const [replyImageError, setReplyImageError] = useState(false);
  
  const originalMessage = useMemo(() => {
    if (!isReply || !originalMessageSid) return null;
    if (replyToMediaUrl) return null; 
    return findOriginalMessage(allMessages, originalMessageSid);
  }, [isReply, originalMessageSid, replyToMediaUrl, allMessages]);

  const effectiveReplyToBody = replyToBody || originalMessage?.body;
  const effectiveReplyToMediaUrl = replyToMediaUrl || originalMessage?.media_url || originalMessage?.imageUrl;
  const effectiveReplyToMediaType = replyToMediaType || originalMessage?.media_type;
  const effectiveReplyToFileName = replyToFileName || originalMessage?.file_name || originalMessage?.fileName;
  
  const measureLines = () => {
    const el = ref.current;
    if (!el) return;
    const style = getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight);
    const lines = Math.round(el.clientHeight / lineHeight);
    setHPadding(lines > 1 ? 18 : 12);
  };

  useLayoutEffect(() => {
    measureLines();
    window.addEventListener("resize", measureLines);
    return () => {
      window.removeEventListener("resize", measureLines);
    };
  }, [body]);

  useLayoutEffect(() => {
    if (sid && ref.current) {
      setMessageRef(sid, ref.current);
    }
    return () => {
      if (sid) {
        setMessageRef(sid, null);
      }
    };
  }, [sid, setMessageRef]);

  const timeString = formatTime(dateSent, t);

  const getWidthStyle = () => {
    if (messageType === "image" || messageType === "audio" || messageType === "assistant" || messageType === "document") {
      return {
        maxWidth: "400px",
        width: "auto",
        minWidth: "300px"
      };
    }
    
    if (from === "user") {
      return {
        maxWidth: "80%",
        width: "auto",
        minWidth: "150px"
      };
    } 
    
    const contentLength = body.length;
    if (contentLength < 30) {
      return {
        width: "auto",
        maxWidth: "300px",
        minWidth: "150px"
      };
    } else if (contentLength < 100) {
      return {
        width: "auto", 
        maxWidth: "50%",
        minWidth: "150px"
      };
    } else if (contentLength < 200) {
      return {
        width: "auto",
        maxWidth: "65%",
        minWidth: "150px"
      };
    } else {
      return {
        width: "auto",
        maxWidth: "75%",
        minWidth: "150px"
      };
    }
  };

  const widthStyle = getWidthStyle();

  const getBackgroundColor = () => {
    if (from === "IA") {
      return "rgba(167, 139, 250, 0.10)"; 
    }
    if (from === "assistant") {
      return "rgba(59, 130, 246, 0.1)"; 
    }
    if (from === "user") {
      return "rgba(229, 231, 235, 0.5)"; 
    }
    return bgColor; 
  };

  const containerBackgroundColor = getBackgroundColor();

  const handleReplyClick = () => {
    if (originalMessageSid) {
      scrollToMessage(originalMessageSid);
    }
  };

  const getReplyMediaType = () => {
    const mediaType = effectiveReplyToMediaType;
    if (!mediaType) return null;
    
    const mediaTypeLower = mediaType.toLowerCase();
    
    if (mediaTypeLower.includes('image') || mediaTypeLower.includes('jpg') || 
        mediaTypeLower.includes('jpeg') || mediaTypeLower.includes('png') || 
        mediaTypeLower.includes('gif') || mediaTypeLower.includes('webp')) {
      return 'image';
    }
    
    if (mediaTypeLower.includes('audio') || mediaTypeLower.includes('ogg') || 
        mediaTypeLower.includes('webm') || mediaTypeLower.includes('mp3') ||
        mediaTypeLower.includes('wav') || mediaTypeLower.includes('m4a') ||
        mediaTypeLower.includes('mpeg') || mediaTypeLower.includes('opus')) {
      return 'audio';
    }
    
    if (mediaTypeLower.includes('pdf') || mediaTypeLower.includes('document') ||
        mediaTypeLower.includes('msword') || mediaTypeLower.includes('wordprocessingml') ||
        mediaTypeLower.includes('sheet') || mediaTypeLower.includes('spreadsheet') ||
        mediaTypeLower.includes('presentation') || mediaTypeLower.includes('text/plain') ||
        mediaTypeLower.includes('zip') || mediaTypeLower.includes('rar')) {
      return 'document';
    }
    
    return null;
  };

  const replyMediaType = getReplyMediaType();
  
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = () => {
    if (!fileName) return <FileText className="w-5 h-5" />;
    const ext = fileName.split('.').pop()?.toLowerCase();
    
    switch (ext) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'doc':
      case 'docx':
        return <FileText className="w-5 h-5 text-blue-500" />;
      case 'xls':
      case 'xlsx':
        return <FileText className="w-5 h-5 text-green-500" />;
      case 'ppt':
      case 'pptx':
        return <FileText className="w-5 h-5 text-orange-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getIconForSender = () => {
    if (from === "user") {
      return <User className="w-4 h-4 text-gray-600" />;
    }
    if (from === "IA") {
      return <Bot className="w-4 h-4 text-purple-600" />;
    }
    if (from === "assistant") {
      return <Headphones className="w-4 h-4 text-blue-600" />;
    }
    return null;
  };
  
  return (
    <div
      ref={ref}
      className={`p-3 rounded-2xl relative transition-all ${alignRight ? 'ml-auto' : 'mr-auto'}`}
      style={{
        paddingLeft: hPadding,
        paddingRight: hPadding,
        paddingBottom: timeString ? 20 : 12,
        paddingTop: 20,
        backgroundColor: containerBackgroundColor,
        ...widthStyle,
      }}
    >
      <div className={`absolute ${alignRight ? '-left-2' : '-right-2'} -top-1 w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm`}>
        {getIconForSender()}
      </div>

      {isReply && (effectiveReplyToBody || effectiveReplyToMediaUrl) && (
        <div 
          className="mb-2 px-3 py-2 rounded-lg border-l-4 cursor-pointer hover:bg-black/10 transition-colors"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            borderLeftColor: 'rgba(0, 0, 0, 0.2)'
          }}
          onClick={handleReplyClick}
          title={t("click_to_view_original") || "Click para ver mensaje original"}
        >
          <div className="text-[10px] font-semibold text-gray-600 mb-1">
            {t("replying_to") || "Respondiendo a"}
          </div>
          
          {replyMediaType === 'image' && effectiveReplyToMediaUrl && (
            <div className="mb-2">
              {!replyImageError ? (
                <img
                  src={effectiveReplyToMediaUrl}
                  alt={effectiveReplyToFileName || "Reply image"}
                  className="max-w-[200px] max-h-[150px] rounded object-cover border border-gray-300 shadow-sm"
                  onError={(e) => {
                    console.error('Reply image failed to load:', effectiveReplyToMediaUrl);
                    setReplyImageError(true);
                  }}
                />
              ) : (
                <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-200 rounded p-2">
                  <span>📷</span>
                  <span>{effectiveReplyToFileName || t("image")}</span>
                </div>
              )}
            </div>
          )}
          
          {replyMediaType === 'audio' && effectiveReplyToMediaUrl && (
            <div className="mb-2">
              <audio 
                controls 
                preload="metadata"
                className="w-full max-w-[250px]"
                style={{ height: '32px' }}
                onError={(e) => {
                  console.error('Reply audio playback error:', {
                    url: effectiveReplyToMediaUrl,
                    error: e,
                    fileName: effectiveReplyToFileName
                  });
                }}
              >
                <source src={effectiveReplyToMediaUrl} type="audio/mpeg" />
                <source src={effectiveReplyToMediaUrl} type="audio/ogg" />
                <source src={effectiveReplyToMediaUrl} type="audio/wav" />
                <source src={effectiveReplyToMediaUrl} type="audio/webm" />
                <source src={effectiveReplyToMediaUrl} type="audio/mp4" />
                {t("audio_not_supported") || "Tu navegador no soporta el elemento de audio."}
              </audio>
            </div>
          )}

          {replyMediaType === 'document' && effectiveReplyToMediaUrl && (
            <div className="mb-2">
              <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-200 rounded p-2">
                <FileText className="w-4 h-4" />
                <span>{effectiveReplyToFileName || t("document")}</span>
              </div>
            </div>
          )}
          
          {effectiveReplyToBody && (
            <div 
              className="text-xs leading-relaxed line-clamp-2"
              style={{ 
                color: 'rgba(0, 0, 0, 0.65)' 
              }}
            >
              {effectiveReplyToBody || ''}
            </div>
          )}
        </div>
      )}
      
      {messageType === "audio" && (mediaUrl || imageUrl) && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 4a3 3 0 016 0v6a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" />
            </svg>
            <div className="flex-1">
              <div className="text-xs font-medium">{t("audio_message") || "Mensaje de audio"}</div>
            </div>
          </div>
          <audio 
            controls 
            preload="metadata"
            className="w-full max-w-xs"
            style={{ height: '40px' }}
            onError={(e) => {
              console.error('Audio playback error:', {
                url: mediaUrl || imageUrl,
                error: e,
                fileName: fileName
              });
            }}
          >
            <source src={mediaUrl || imageUrl} type="audio/mpeg" />
            <source src={mediaUrl || imageUrl} type="audio/ogg" />
            <source src={mediaUrl || imageUrl} type="audio/wav" />
            <source src={mediaUrl || imageUrl} type="audio/webm" />
            <source src={mediaUrl || imageUrl} type="audio/mp4" />
            {t("audio_not_supported") || "Tu navegador no soporta el elemento de audio."}
          </audio>
        </div>
      )}

      {messageType === "document" && (mediaUrl || imageUrl) && (
        <div className="space-y-2">
          <a
            href={mediaUrl || imageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            <div className="flex-shrink-0">
              {getFileIcon()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-700 truncate">
                {fileName || "document"}
              </div>
              <div className="text-xs text-gray-500">
                {t("click_to_download") || "Click para descargar"}
              </div>
            </div>
            <Download className="w-4 h-4 text-gray-400 flex-shrink-0" />
          </a>
        </div>
      )}
      
      {messageType === "image" && imageUrl && (
        <div className="space-y-2">
          {!imageError ? (
            <img
              src={imageUrl}
              alt={fileName || "Imagen"}
              className="max-w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => window.open(imageUrl, '_blank')}
              onError={() => setImageError(true)}
              style={{ maxHeight: '300px', objectFit: 'contain' }}
            />
          ) : (
            <div className="flex items-center justify-center bg-gray-100 rounded-lg p-4 min-h-[100px]">
              <div className="text-center text-gray-500">
                <div className="text-sm">📷</div>
                <div className="text-xs mt-1">{t("image_error")}</div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {messageType !== "image" && messageType !== "audio" && messageType !== "document" && (
        <p className="text-sm whitespace-pre-wrap break-words mb-1" style={{ color: textColor }}>
          {from === "user" ? (body || '') : formatMessage(body || '')}
        </p>
      )}
      
      {timeString && (
        <span 
          className={`absolute text-xs opacity-60 ${alignRight ? 'right-3' : 'left-3'} ${(messageType === "image" || messageType === "audio" || messageType === "document") ? 'bottom-1' : 'bottom-2'}`}
          style={{ color: textColor }}
        >
          {timeString}
        </span>
      )}
    </div>
  );
};

const ChatWindow: React.FC<ChatWindowProps> = ({ messages, isHistoryPage }) => {
  const { t } = useTranslation();
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const scrollToMessage = useCallback((sid: string) => {

    const element = messageRefs.current[sid];
    if (element && viewportRef.current) {

      const container = viewportRef.current;
      const elementRect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      
      const scrollTop = element.offsetTop - container.offsetTop - (container.clientHeight / 2) + (element.clientHeight / 2);
      
      container.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });
      
      // Agregar highlight
      element.classList.add('highlight-message');
      setTimeout(() => {
        element.classList.remove('highlight-message');
      }, 2000);
    } else {
      console.error('❌ Element not found for sid:', sid);
    }
  }, []);

  const setMessageRef = useCallback((sid: string, el: HTMLDivElement | null) => {
    if (el) {
      messageRefs.current[sid] = el;
    } else {
      delete messageRefs.current[sid];
    }
  }, []);

  useLayoutEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;

    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => {
        vp.scrollTop = vp.scrollHeight;
      });
      (vp as any).__r2 = r2;
    });

    return () => {
      cancelAnimationFrame(r1);
      if ((vp as any).__r2) cancelAnimationFrame((vp as any).__r2);
    };
  }, [messages.length]);

  const containerBg = isHistoryPage ? "bg-[#F6F7F9]" : "bg-white";

  return (
    <div ref={viewportRef} className={`flex-1 min-h-0 overflow-y-auto ${containerBg} custom-scrollbar`}>
      <style>{`
        .highlight-message {
          animation: highlight 2s ease-in-out;
        }
        
        @keyframes highlight {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.3);
          }
        }
      `}</style>
      <div className="flex flex-col gap-2 p-4">
        {messages.map((msg, idx) => {
          let bgColor = "#ffffff";
          let textColor = "#000000";
          const alignRight = isHistoryPage ? msg.from !== "user" : msg.type === "sent"; 

          if (isHistoryPage) {
            if (msg.from === "user") {
              bgColor = "#E5E7EB"; 
            } else {
              bgColor = "#FFFFFF";
            }
          } else {
            if (msg.type === "sent") {
              bgColor = "#F8F9FB";
            } else if (msg.type === "received") {
              bgColor = "#EDEFF2";
            }
          }

          return (
            <div key={idx} className={`flex ${alignRight ? "justify-end" : "justify-start"}`}>
              <MessageBubble
                from={msg.from}
                body={msg.body}
                bgColor={bgColor}
                textColor={textColor}
                alignRight={alignRight}
                dateSent={msg.dateSent}
                isReply={msg.isReply}
                replyToBody={msg.replyToBody}
                replyToMediaUrl={msg.replyToMediaUrl}
                replyToMediaType={msg.replyToMediaType}
                replyToFileName={msg.replyToFileName}
                imageUrl={msg.imageUrl || msg.media_url}
                fileName={msg.fileName || msg.file_name}
                messageType={msg.messageType}
                mediaUrl={msg.media_url || msg.imageUrl}
                originalMessageSid={msg.originalMessageSid}
                sid={msg.sid}
                setMessageRef={setMessageRef}
                scrollToMessage={scrollToMessage}
                t={t}
                allMessages={messages}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChatWindow;
